// QQCARD mock API service layer
// All product reads return immediately from local catalog (simulates SSR/cache hits).
// Mutations (login, place order, subscribe) simulate network latency for realistic UX.
//
// This layer is designed so that swapping to a real REST/GraphQL backend
// only requires replacing implementations here — components don't change.

import { PRODUCTS } from '../data/catalog';

const wait = (ms) => new Promise((res) => setTimeout(res, ms));
const jitter = (min, max) => Math.random() * (max - min) + min;

// Track recent calls for client-side rate limiting
const callLog = new Map();
const rateLimit = (key, ms) => {
  const last = callLog.get(key) || 0;
  const now = Date.now();
  if (now - last < ms) {
    return false;
  }
  callLog.set(key, now);
  return true;
};

const safeMatch = (q) => (p) => {
  const Q = q.toLowerCase();
  return (
    p.name.toLowerCase().includes(Q) ||
    p.en.toLowerCase().includes(Q) ||
    p.publisher.toLowerCase().includes(Q) ||
    (p.tagline || '').toLowerCase().includes(Q)
  );
};

export const api = {
  /* ============ Catalog ============ */
  async fetchProducts({ category, region, query, sort } = {}) {
    await wait(jitter(120, 280));
    let list = PRODUCTS.slice();
    if (category && category !== 'all') list = list.filter((p) => p.category === category);
    if (region && region !== 'all') list = list.filter((p) => p.region === region);
    if (query?.trim()) list = list.filter(safeMatch(query.trim()));
    if (sort === 'priceAsc')
      list.sort((a, b) => a.denominations[0].price - b.denominations[0].price);
    else if (sort === 'priceDesc')
      list.sort((a, b) => b.denominations[0].price - a.denominations[0].price);
    else if (sort === 'discount')
      list.sort((a, b) => (b.discount || 0) - (a.discount || 0));
    return { items: list, total: list.length };
  },

  async fetchProduct(id) {
    await wait(jitter(80, 180));
    const product = PRODUCTS.find((p) => p.id === id);
    if (!product) {
      const err = new Error('NOT_FOUND');
      err.code = 404;
      throw err;
    }
    return product;
  },

  /* ============ Auth ============ */
  async signIn({ email, password }) {
    if (!rateLimit('signIn:' + email, 1500)) {
      const err = new Error('RATE_LIMIT');
      err.code = 429;
      throw err;
    }
    await wait(jitter(700, 1100));
    if (!email?.includes('@')) {
      const err = new Error('INVALID_EMAIL');
      err.code = 400;
      throw err;
    }
    if ((password || '').length < 6) {
      const err = new Error('WEAK_PASSWORD');
      err.code = 400;
      throw err;
    }
    return {
      user: {
        id: 'u_' + Math.random().toString(36).slice(2, 10),
        email,
        name: email.split('@')[0],
        avatar: '🦊',
        nexusPoints: 0,
        lifetimeSpent: 0,
        joinedAt: new Date().toISOString().slice(0, 10),
      },
      token: 'tk_' + Math.random().toString(36).slice(2, 24),
    };
  },

  async signUp({ email, password }) {
    return this.signIn({ email, password });
  },

  /* ============ Orders ============ */
  async placeOrder({ items, paymentMethod, contact }) {
    if (!items?.length) {
      const err = new Error('EMPTY_CART');
      err.code = 400;
      throw err;
    }
    if (!paymentMethod) {
      const err = new Error('MISSING_PAYMENT');
      err.code = 400;
      throw err;
    }
    // Simulate payment processing
    await wait(jitter(1200, 1800));
    // 1% simulated failure for realism (disabled in this build)
    // if (Math.random() < 0.01) throw new Error('PAYMENT_DECLINED');
    return {
      id: 'NX' + Date.now().toString(36).toUpperCase(),
      status: 'paid',
      paidAt: new Date().toISOString(),
      paymentMethod,
      contact,
      items: items.map((it) => ({
        ...it,
        serial:
          'NX-' +
          Math.random().toString(36).slice(2, 6).toUpperCase() +
          '-' +
          Math.random().toString(36).slice(2, 6).toUpperCase() +
          '-' +
          Math.random().toString(36).slice(2, 6).toUpperCase(),
      })),
    };
  },

  /* ============ Newsletter ============ */
  async subscribeNewsletter(email) {
    if (!rateLimit('news:' + email, 5000)) {
      const err = new Error('RATE_LIMIT');
      err.code = 429;
      throw err;
    }
    await wait(jitter(400, 700));
    if (!email?.includes('@')) {
      const err = new Error('INVALID_EMAIL');
      err.code = 400;
      throw err;
    }
    return { ok: true, subscribedAt: new Date().toISOString() };
  },

};

export default api;
